import { useState, useEffect, useCallback } from "react";
import { useFetcher } from "react-router-dom";
import toast from "react-hot-toast";
import { Button } from "../common";
import type { NetworkTierResponse } from "../../types/api";
import type { ActionResult } from "../../routes/actions";
import styles from "./TierManagement.module.css";

interface TierManagementProps {
  organizationId: string;
  networkId: string;
  tiers: NetworkTierResponse[];
}

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function TierManagement({ organizationId, networkId, tiers }: TierManagementProps) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTierId, setEditingTierId] = useState<string | null>(null);
  const [deletingTierId, setDeletingTierId] = useState<string | null>(null);

  const createFetcher = useFetcher();
  const updateFetcher = useFetcher();
  const deleteFetcher = useFetcher();
  const reorderFetcher = useFetcher();

  const basePath = `/organizations/${organizationId}/networks/${networkId}`;

  // Handle create result
  useEffect(() => {
    if (createFetcher.data) {
      const result = createFetcher.data as ActionResult;
      if (result.success) {
        toast.success("Tier created successfully!");
        setShowCreateForm(false);
      } else if (result.error) {
        toast.error(result.error);
      }
    }
  }, [createFetcher.data]);

  // Handle update result
  useEffect(() => {
    if (updateFetcher.data) {
      const result = updateFetcher.data as ActionResult;
      if (result.success) {
        toast.success("Tier updated successfully!");
        setEditingTierId(null);
      } else if (result.error) {
        toast.error(result.error);
      }
    }
  }, [updateFetcher.data]);

  // Handle delete result
  useEffect(() => {
    if (deleteFetcher.data) {
      const result = deleteFetcher.data as ActionResult;
      if (result.success) {
        toast.success("Tier deleted successfully!");
        setDeletingTierId(null);
      } else if (result.error) {
        toast.error(result.error);
        setDeletingTierId(null);
      }
    }
  }, [deleteFetcher.data]);

  // Handle reorder result
  useEffect(() => {
    if (reorderFetcher.data) {
      const result = reorderFetcher.data as ActionResult;
      if (result.success) {
        toast.success("Tiers reordered!");
      } else if (result.error) {
        toast.error(result.error);
      }
    }
  }, [reorderFetcher.data]);

  const handleMove = useCallback(
    (tierId: string, direction: "up" | "down") => {
      const currentIndex = tiers.findIndex((t) => t.id === tierId);
      if (currentIndex === -1) return;

      const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= tiers.length) return;

      const reordered = [...tiers];
      const [moved] = reordered.splice(currentIndex, 1);
      reordered.splice(newIndex, 0, moved);

      const formData = new FormData();
      formData.set("tierIds", JSON.stringify(reordered.map((t) => t.id)));

      reorderFetcher.submit(formData, {
        method: "post",
        action: `${basePath}/tiers/reorder`,
      });
    },
    [tiers, reorderFetcher, basePath],
  );

  const isCreating = createFetcher.state === "submitting";
  const isUpdating = updateFetcher.state === "submitting";
  const isDeleting = deleteFetcher.state === "submitting";

  return (
    <div className={styles.section}>
      <h2>Subscription Tiers</h2>
      <p className={styles.sectionDescription}>
        Define pricing tiers for your network. Higher tiers inherit access to all content from lower
        tiers.
      </p>

      {tiers.length > 0 ? (
        <div className={styles.tierList}>
          {tiers.map((tier, index) => (
            <div key={tier.id} className={styles.tierCard}>
              <div className={styles.reorderButtons}>
                <button
                  type="button"
                  className={styles.reorderBtn}
                  disabled={index === 0}
                  onClick={() => handleMove(tier.id, "up")}
                  title="Move up"
                >
                  ▲
                </button>
                <button
                  type="button"
                  className={styles.reorderBtn}
                  disabled={index === tiers.length - 1}
                  onClick={() => handleMove(tier.id, "down")}
                  title="Move down"
                >
                  ▼
                </button>
              </div>

              <div className={styles.tierInfo}>
                <h3 className={styles.tierName}>{tier.name}</h3>
                {tier.description && <p className={styles.tierDescription}>{tier.description}</p>}
                <div className={styles.tierPricing}>
                  <span className={styles.tierPrice}>
                    <strong>{formatPrice(tier.monthlyPriceCents)}</strong>/mo
                  </span>
                  <span className={styles.tierPrice}>
                    <strong>{formatPrice(tier.annualPriceCents)}</strong>/yr
                  </span>
                </div>
                {index > 0 && (
                  <div className={styles.inheritanceIndicator}>
                    <span className={styles.inheritanceIcon}>⊃</span>
                    Includes all content from:{" "}
                    {tiers
                      .slice(0, index)
                      .map((t) => t.name)
                      .join(", ")}
                  </div>
                )}
              </div>

              <div className={styles.tierActions}>
                <button
                  type="button"
                  className={styles.tierActionBtn}
                  onClick={() => setEditingTierId(editingTierId === tier.id ? null : tier.id)}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className={styles.tierActionBtnDanger}
                  onClick={() => setDeletingTierId(deletingTierId === tier.id ? null : tier.id)}
                >
                  Delete
                </button>
              </div>

              {/* Inline edit form */}
              {editingTierId === tier.id && (
                <div className={styles.formOverlay} style={{ width: "100%" }}>
                  <h3>Edit Tier</h3>
                  <updateFetcher.Form
                    method="post"
                    action={`${basePath}/tiers/${tier.id}/edit`}
                    className={styles.form}
                  >
                    <div className={styles.formGroup}>
                      <label htmlFor={`edit-name-${tier.id}`}>Name</label>
                      <input
                        id={`edit-name-${tier.id}`}
                        name="name"
                        type="text"
                        defaultValue={tier.name}
                        disabled={isUpdating}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor={`edit-desc-${tier.id}`}>Description</label>
                      <textarea
                        id={`edit-desc-${tier.id}`}
                        name="description"
                        defaultValue={tier.description || ""}
                        rows={2}
                        disabled={isUpdating}
                      />
                    </div>
                    <div className={styles.priceRow}>
                      <div className={styles.formGroup}>
                        <label htmlFor={`edit-monthly-${tier.id}`}>Monthly Price (cents)</label>
                        <input
                          id={`edit-monthly-${tier.id}`}
                          name="monthlyPriceCents"
                          type="number"
                          min="0"
                          defaultValue={tier.monthlyPriceCents}
                          disabled={isUpdating}
                        />
                      </div>
                      <div className={styles.formGroup}>
                        <label htmlFor={`edit-annual-${tier.id}`}>Annual Price (cents)</label>
                        <input
                          id={`edit-annual-${tier.id}`}
                          name="annualPriceCents"
                          type="number"
                          min="0"
                          defaultValue={tier.annualPriceCents}
                          disabled={isUpdating}
                        />
                      </div>
                    </div>
                    <div className={styles.formActions}>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setEditingTierId(null)}
                        disabled={isUpdating}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        loading={isUpdating}
                        loadingText="Saving..."
                      >
                        Save Changes
                      </Button>
                    </div>
                  </updateFetcher.Form>
                </div>
              )}

              {/* Delete confirmation */}
              {deletingTierId === tier.id && (
                <div className={styles.deleteConfirm}>
                  <p className={styles.deleteConfirmText}>
                    Are you sure you want to delete the &quot;{tier.name}&quot; tier? Any active
                    subscribers will need to be migrated first.
                  </p>
                  <div className={styles.deleteConfirmActions}>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setDeletingTierId(null)}
                      disabled={isDeleting}
                    >
                      Cancel
                    </Button>
                    <deleteFetcher.Form
                      method="post"
                      action={`${basePath}/tiers/${tier.id}/delete`}
                    >
                      <Button
                        type="submit"
                        variant="danger"
                        loading={isDeleting}
                        loadingText="Deleting..."
                      >
                        Delete Tier
                      </Button>
                    </deleteFetcher.Form>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.emptyState}>
          <p>No tiers configured yet. Create your first tier to start monetizing your network.</p>
        </div>
      )}

      {!showCreateForm ? (
        <Button type="button" variant="primary" onClick={() => setShowCreateForm(true)}>
          Add Tier
        </Button>
      ) : (
        <div className={styles.formOverlay}>
          <h3>Create New Tier</h3>
          <createFetcher.Form
            method="post"
            action={`${basePath}/tiers/create`}
            className={styles.form}
          >
            <div className={styles.formGroup}>
              <label htmlFor="create-tier-name">Name</label>
              <input
                id="create-tier-name"
                name="name"
                type="text"
                placeholder="e.g., Basic, Premium, VIP"
                disabled={isCreating}
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="create-tier-desc">Description</label>
              <textarea
                id="create-tier-desc"
                name="description"
                placeholder="What's included in this tier..."
                rows={2}
                disabled={isCreating}
              />
            </div>
            <div className={styles.priceRow}>
              <div className={styles.formGroup}>
                <label htmlFor="create-tier-monthly">Monthly Price (cents)</label>
                <input
                  id="create-tier-monthly"
                  name="monthlyPriceCents"
                  type="number"
                  min="0"
                  placeholder="999"
                  disabled={isCreating}
                  required
                />
                <span className={styles.helpText}>e.g., 999 = $9.99/mo</span>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="create-tier-annual">Annual Price (cents)</label>
                <input
                  id="create-tier-annual"
                  name="annualPriceCents"
                  type="number"
                  min="0"
                  placeholder="9999"
                  disabled={isCreating}
                  required
                />
                <span className={styles.helpText}>e.g., 9999 = $99.99/yr</span>
              </div>
            </div>
            <div className={styles.formActions}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowCreateForm(false)}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={isCreating}
                loadingText="Creating..."
              >
                Create Tier
              </Button>
            </div>
          </createFetcher.Form>
        </div>
      )}
    </div>
  );
}
